import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { 
    Waves, Activity, Sparkles, ChevronRight, ArrowRight, 
    Check, Zap, Shield, Clock, Eye, Hand, Ear
} from 'lucide-react';
import CoRegulateLogo from '../components/Logo';
import { SmallText } from '../components/brand/BrandTypography';

// JSON-LD Structured Data for SEO
const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
        {
            "@type": "WebPage",
            "@id": "https://coregulateadhd.com/adhd-grounding-techniques",
            "url": "https://coregulateadhd.com/adhd-grounding-techniques",
            "name": "ADHD Grounding Techniques & Breathing Exercises | CoRegulate",
            "description": "Evidence-based breathing exercises and grounding techniques adapted for ADHD nervous systems. Find calm in 30 seconds or less.",
            "isPartOf": { "@id": "https://coregulateadhd.com/#website" },
            "about": { "@id": "https://coregulateadhd.com/#organization" }
        },
        {
            "@type": "Organization",
            "@id": "https://coregulateadhd.com/#organization",
            "name": "CoRegulate",
            "url": "https://coregulateadhd.com",
            "description": "Made for ADHD hearts, shaped by ADHD minds."
        },
        {
            "@type": "FAQPage",
            "mainEntity": [
                {
                    "@type": "Question",
                    "name": "What are the best grounding techniques for ADHD?",
                    "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "The best grounding techniques for ADHD are quick (30 seconds to 5 minutes), visually guided, and require minimal instructions. Top techniques include: Physiological Sigh (double inhale, long exhale - 30 seconds), Box Breathing (4-4-4-4 pattern), 5-4-3-2-1 Sensory Grounding, and Micro Reset (quick sensory grounding using sight, sound, touch, and breath)."
                    }
                },
                {
                    "@type": "Question",
                    "name": "Why does grounding work for ADHD?",
                    "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "Grounding works for ADHD because it speaks directly to the nervous system through sensation. Extended exhale activates the vagus nerve, sensory focus interrupts rumination loops, physical awareness reduces dissociation, and rhythm creates predictability for anxious brains. Quick techniques (30 seconds) work with short attention spans, and visual guidance keeps focus."
                    }
                },
                {
                    "@type": "Question",
                    "name": "How do I calm my ADHD brain quickly?",
                    "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "The fastest way to calm an ADHD brain is the Physiological Sigh: take two quick inhales through your nose, then one long exhale through your mouth. This takes only 30 seconds and immediately activates your parasympathetic nervous system. For longer calm, try Box Breathing (inhale 4, hold 4, exhale 4, hold 4) for 2-5 minutes."
                    }
                }
            ]
        },
        {
            "@type": "HowTo",
            "name": "How to Calm Your ADHD Nervous System",
            "description": "Quick grounding techniques for ADHD emotional regulation",
            "totalTime": "PT5M",
            "step": [
                {
                    "@type": "HowToStep",
                    "name": "Physiological Sigh",
                    "text": "Double inhale through the nose, long exhale through the mouth. Takes 30 seconds. Best for quick stress relief."
                },
                {
                    "@type": "HowToStep",
                    "name": "Box Breathing",
                    "text": "Inhale 4 seconds, hold 4, exhale 4, hold 4. Creates rhythm and focus. Takes 2-5 minutes."
                },
                {
                    "@type": "HowToStep",
                    "name": "5-4-3-2-1 Grounding",
                    "text": "Notice 5 things you see, 4 you hear, 3 you feel, 2 you smell, 1 you taste. Best for panic and dissociation."
                },
                {
                    "@type": "HowToStep",
                    "name": "Extended Exhale",
                    "text": "Make your exhale longer than your inhale by even 1 second. This signals safety to your nervous system."
                }
            ]
        },
        {
            "@type": "ItemList",
            "name": "ADHD Grounding Techniques",
            "itemListElement": [
                {
                    "@type": "ListItem",
                    "position": 1,
                    "name": "Physiological Sigh",
                    "description": "Double inhale through the nose, long exhale through the mouth. 30 seconds for quick stress relief."
                },
                {
                    "@type": "ListItem",
                    "position": 2,
                    "name": "Box Breathing",
                    "description": "Inhale 4 seconds, hold 4, exhale 4, hold 4. 2-5 minutes for anxiety and racing thoughts."
                },
                {
                    "@type": "ListItem",
                    "position": 3,
                    "name": "4-7-8 Breathing",
                    "description": "Inhale 4, hold 7, exhale 8. 3-5 minutes for deep relaxation and sleep."
                },
                {
                    "@type": "ListItem",
                    "position": 4,
                    "name": "Micro Reset",
                    "description": "Quick sensory grounding using sight, sound, touch, and breath. 30 seconds for immediate relief."
                },
                {
                    "@type": "ListItem",
                    "position": 5,
                    "name": "5-4-3-2-1 Grounding",
                    "description": "Notice 5 things you see, 4 you hear, 3 you feel, 2 you smell, 1 you taste. 2-3 minutes for panic and dissociation."
                },
                {
                    "@type": "ListItem",
                    "position": 6,
                    "name": "Body Scan",
                    "description": "Guided attention through each body part to release tension and reconnect. 5-10 minutes for body disconnection."
                }
            ]
        }
    ]
};

const SEOLandingGroundingTechniques = () => {
    const navigate = useNavigate();
    
    const techniques = [
        {
            name: 'Physiological Sigh',
            description: 'Double inhale through the nose, long exhale through the mouth. Instantly calms your nervous system.',
            time: '30 seconds',
            icon: Waves,
            best: 'Quick stress relief'
        },
        {
            name: 'Box Breathing',
            description: 'Inhale 4 seconds, hold 4, exhale 4, hold 4. Creates rhythm and focus.',
            time: '2-5 minutes',
            icon: Activity,
            best: 'Anxiety and racing thoughts'
        },
        {
            name: '4-7-8 Breathing',
            description: 'Inhale 4, hold 7, exhale 8. Activates your parasympathetic nervous system.',
            time: '3-5 minutes',
            icon: Waves,
            best: 'Deep relaxation and sleep'
        },
        {
            name: 'Micro Reset',
            description: 'Quick sensory grounding using sight, sound, touch, and breath.',
            time: '30 seconds',
            icon: Zap,
            best: 'Immediate relief'
        },
        {
            name: '5-4-3-2-1 Grounding',
            description: 'Notice 5 things you see, 4 you hear, 3 you feel, 2 you smell, 1 you taste.',
            time: '2-3 minutes',
            icon: Eye,
            best: 'Panic and dissociation'
        },
        {
            name: 'Body Scan',
            description: 'Guided attention through each body part to release tension and reconnect.',
            time: '5-10 minutes',
            icon: Shield,
            best: 'Body disconnection'
        }
    ];
    
    const senses = [
        { sense: 'Sight', icon: Eye, tip: 'Focus on a single point, notice colors and shapes around you' },
        { sense: 'Touch', icon: Hand, tip: 'Feel your feet on the ground, hands on your legs, texture of clothing' },
        { sense: 'Sound', icon: Ear, tip: 'Listen for the quietest sound you can hear' },
        { sense: 'Breath', icon: Waves, tip: 'Slow exhale is the fastest way to signal safety to your brain' }
    ];

    return (
        <div className="min-h-screen bg-white/60">
            {/* SEO Meta Tags and Structured Data */}
            <Helmet>
                <title>ADHD Grounding Techniques & Breathing Exercises | CoRegulate</title>
                <meta name="description" content="Evidence-based breathing exercises and grounding techniques adapted for ADHD nervous systems. Find calm in 30 seconds or less." />
                <meta name="keywords" content="ADHD grounding techniques, ADHD breathing exercises, ADHD calming techniques, box breathing ADHD, grounding for anxiety" />
                <link rel="canonical" href="https://coregulateadhd.com/adhd-grounding-techniques" />
                <meta property="og:title" content="ADHD Grounding Techniques & Breathing Exercises | CoRegulate" />
                <meta property="og:description" content="Evidence-based breathing exercises and grounding techniques adapted for ADHD nervous systems. Find calm in 30 seconds or less." />
                <meta property="og:url" content="https://coregulateadhd.com/adhd-grounding-techniques" />
                <meta property="og:type" content="website" />
            </Helmet>
            
            {/* JSON-LD Structured Data */}
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />
            
            {/* Header */}
            <header className="sticky top-0 z-50 bg-white/60/95 backdrop-blur-sm border-b border-white/40/30">
                <nav className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
                    <Link to="/landing" className="flex items-center gap-2">
                        <CoRegulateLogo size="md" />
                    </Link>
                    <div className="flex items-center gap-3">
                        <Link 
                            to="/auth?mode=login"
                            className="hidden sm:flex px-4 py-2 text-sm text-[#7D8E95] hover:text-[#1D6B6B] transition-colors"
                        >
                            Sign in
                        </Link>
                        <Link 
                            to="/initial-assessment"
                            className="px-5 py-2.5 bg-gradient-to-r from-[#1D6B6B] to-[#145555] text-white text-sm font-medium rounded-full hover:shadow-lg transition-all"
                            data-testid="nav-cta"
                        >
                            Try Free
                        </Link>
                    </div>
                </nav>
            </header>
            
            {/* Hero Section */}
            <section className="py-16 md:py-24 overflow-hidden">
                <div className="max-w-4xl mx-auto px-4 text-center">
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#7D8E95]/10 rounded-full mb-6">
                        <Waves className="w-4 h-4 text-[#7D8E95]" />
                        <SmallText className="text-[#7D8E95] font-medium">Calm Your Nervous System</SmallText>
                    </div>
                    
                    <h1 className="text-4xl md:text-5xl lg:text-6xl font-semibold text-[#1D6B6B] mb-6" style={{ fontFamily: "'Lora', Georgia, serif" }}>
                        ADHD Grounding Techniques
                    </h1>
                    
                    <p className="text-lg md:text-xl text-[#7D8E95] max-w-2xl mx-auto mb-8">
                        Evidence-based breathing exercises and grounding techniques adapted for ADHD nervous systems. 
                        Find calm in 30 seconds or less.
                    </p>
                    
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <button 
                            onClick={() => navigate('/initial-assessment')}
                            className="group px-8 py-4 bg-gradient-to-r from-[#1D6B6B] to-[#145555] text-white font-medium rounded-full hover:shadow-lg transition-all flex items-center justify-center gap-2"
                            data-testid="hero-cta"
                        >
                            Start Grounding
                            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </button>
                        <button 
                            onClick={() => navigate('/adhd-emotional-regulation')}
                            className="px-8 py-4 bg-white/70 text-[#1D6B6B] font-medium rounded-full border border-[#7D8E95]/30 hover:bg-white transition-all"
                        >
                            All Regulation Tools
                        </button>
                    </div>
                </div>
            </section>
            
            {/* Why Grounding Works */}
            <section className="py-16 bg-white/70">
                <div className="max-w-4xl mx-auto px-4">
                    <div className="text-center mb-12">
                        <h2 className="text-2xl md:text-3xl font-semibold text-[#1D6B6B] mb-4" style={{ fontFamily: "'Lora', Georgia, serif" }}>
                            Why Grounding Works for ADHD
                        </h2>
                        <p className="text-[#7D8E95] max-w-2xl mx-auto">
                            Your nervous system speaks a language of sensation. These techniques speak directly to your body.
                        </p>
                    </div>
                    
                    <div className="grid md:grid-cols-2 gap-6">
                        <div className="bg-white/60 rounded-2xl p-6 border border-white/40/20">
                            <h3 className="font-medium text-[#1D6B6B] mb-3">The Science</h3>
                            <ul className="space-y-3 text-[#7D8E95]">
                                <li className="flex items-start gap-2">
                                    <span className="text-[#1D6B6B] mt-1">•</span>
                                    Extended exhale activates your vagus nerve
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-[#1D6B6B] mt-1">•</span>
                                    Sensory focus interrupts rumination loops
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-[#1D6B6B] mt-1">•</span>
                                    Physical awareness reduces dissociation
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-[#1D6B6B] mt-1">•</span>
                                    Rhythm creates predictability for anxious brains
                                </li>
                            </ul>
                        </div>
                        
                        <div className="bg-white/60 rounded-2xl p-6 border border-white/40/20">
                            <h3 className="font-medium text-[#1D6B6B] mb-3">For ADHD Specifically</h3>
                            <ul className="space-y-3 text-[#7D8E95]">
                                <li className="flex items-start gap-2">
                                    <span className="text-[#FFBB98] mt-1">•</span>
                                    Quick techniques (30 sec) work with short attention spans
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-[#FFBB98] mt-1">•</span>
                                    Visual guidance keeps focus
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-[#FFBB98] mt-1">•</span>
                                    One action per step prevents overwhelm
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-[#FFBB98] mt-1">•</span>
                                    No complicated instructions to remember
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </section>
            
            {/* Techniques Grid */}
            <section className="py-16">
                <div className="max-w-5xl mx-auto px-4">
                    <div className="text-center mb-12">
                        <h2 className="text-2xl md:text-3xl font-semibold text-[#1D6B6B] mb-4" style={{ fontFamily: "'Lora', Georgia, serif" }}>
                            Grounding Techniques
                        </h2>
                        <p className="text-[#7D8E95]">
                            From 30-second quick resets to deeper body practices
                        </p>
                    </div>
                    
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {techniques.map((technique, index) => (
                            <div 
                                key={technique.name}
                                className="bg-white/60 rounded-2xl p-6 border border-white/40/20 hover:shadow-lg hover:-translate-y-1 transition-all"
                            >
                                <div className="flex items-center justify-between mb-4">
                                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#7D8E95]/10 to-[#7D8E95]/20 flex items-center justify-center">
                                        <technique.icon className="w-6 h-6 text-[#7D8E95]" />
                                    </div>
                                    <div className="flex items-center gap-1 text-xs text-[#7D8E95] bg-white/70 px-2 py-1 rounded-full">
                                        <Clock className="w-3 h-3" />
                                        {technique.time}
                                    </div>
                                </div>
                                <h3 className="font-medium text-[#1D6B6B] mb-2">{technique.name}</h3>
                                <p className="text-sm text-[#7D8E95] mb-3">{technique.description}</p>
                                <div className="text-xs text-[#1D6B6B] font-medium">
                                    Best for: {technique.best}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
            
            {/* Sensory Grounding */}
            <section className="py-16 bg-white/70">
                <div className="max-w-4xl mx-auto px-4">
                    <div className="text-center mb-12">
                        <h2 className="text-2xl md:text-3xl font-semibold text-[#1D6B6B] mb-4" style={{ fontFamily: "'Lora', Georgia, serif" }}>
                            Sensory Grounding Tips
                        </h2>
                        <p className="text-[#7D8E95]">
                            Quick ways to anchor yourself in the present moment
                        </p>
                    </div>
                    
                    <div className="grid sm:grid-cols-2 gap-4">
                        {senses.map((item, index) => (
                            <div 
                                key={item.sense}
                                className="flex items-start gap-4 bg-white/60 rounded-xl p-5 border border-white/40/20"
                            >
                                <div className="w-10 h-10 rounded-lg bg-[#1D6B6B]/10 flex items-center justify-center flex-shrink-0">
                                    <item.icon className="w-5 h-5 text-[#1D6B6B]" />
                                </div>
                                <div>
                                    <h3 className="font-medium text-[#1D6B6B] mb-1">{item.sense}</h3>
                                    <p className="text-sm text-[#7D8E95]">{item.tip}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                    
                    <div className="mt-8 p-6 bg-[#1D6B6B]/5 rounded-2xl border border-[#1D6B6B]/10 text-center">
                        <p className="text-[#1D6B6B] font-medium mb-2">
                            Pro tip: Extended exhale is your superpower
                        </p>
                        <p className="text-sm text-[#7D8E95]">
                            Making your exhale longer than your inhale (even by 1 second) signals safety to your nervous system.
                        </p>
                    </div>
                </div>
            </section>
            
            {/* CTA Section */}
            <section className="py-20 bg-gradient-to-br from-[#1D6B6B] to-[#145555]">
                <div className="max-w-3xl mx-auto px-4 text-center">
                    <Activity className="w-12 h-12 text-white/80 mx-auto mb-6" />
                    <h2 className="text-3xl font-semibold text-white mb-4" style={{ fontFamily: "'Lora', Georgia, serif" }}>
                        Find Your Calm
                    </h2>
                    <p className="text-white/80 mb-8 max-w-xl mx-auto">
                        Take a quick assessment and we'll recommend the right grounding technique for your current state.
                    </p>
                    <button 
                        onClick={() => navigate('/initial-assessment')}
                        className="group px-10 py-4 bg-white text-[#1D6B6B] font-semibold rounded-full hover:bg-white/60 hover:shadow-lg transition-all flex items-center justify-center gap-2 mx-auto"
                        data-testid="final-cta"
                    >
                        Start Grounding Now
                        <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </button>
                </div>
            </section>
            
            {/* Footer */}
            <footer className="bg-[#145555] text-white py-12">
                <div className="max-w-4xl mx-auto px-4 text-center">
                    <CoRegulateLogo size="small" variant="light" />
                    <p className="text-white/60 text-sm mt-4 mb-6">
                        Made for ADHD hearts, shaped by ADHD minds.
                    </p>
                    <div className="flex justify-center gap-6 text-sm text-white/60">
                        <Link to="/landing" className="hover:text-white transition-colors">Home</Link>
                        <Link to="/blog" className="hover:text-white transition-colors">Blog</Link>
                        <Link to="/adhd-emotional-regulation" className="hover:text-white transition-colors">Regulation Tools</Link>
                        <Link to="/adhd-relationship-tools" className="hover:text-white transition-colors">Relationship Tools</Link>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default SEOLandingGroundingTechniques;
